<template>
    <div class="pa-4">
        <v-overlay :value="loadingRecipes">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <p class="recipe-hint">
            {{
                "A work order keeps the recipe version that was valid when it came into Tootja. To change quantities, add a new version."
                    | t
            }}
        </p>

        <v-card v-for="recipe in recipes" :key="'recipe_' + recipe.id" flat outlined class="mb-6">
            <v-card-title class="py-2">
                <span>{{ recipe.code }}</span>
                <span class="recipe-name ml-3">{{ recipe.name }}</span>
                <v-spacer />
                <v-btn
                    v-if="hasPermissionForAction(PermissionsConstants.RECIPE, PermissionsConstants.CREATE)"
                    small
                    dark
                    color="bal-red"
                    @click="openVersionModal(recipe)"
                >
                    <v-icon small left>add</v-icon>
                    {{ "New version" | t }}
                </v-btn>
            </v-card-title>

            <v-simple-table dense>
                <thead>
                    <tr>
                        <th>{{ "Version" | t }}</th>
                        <th>{{ "Valid from" | t }}</th>
                        <th>{{ "Valid to" | t }}</th>
                        <th v-for="column in ingredientColumns(recipe)" :key="column.product_code" class="text-right">
                            {{ column.product_name }} ({{ column.product_code }}), kg
                        </th>
                        <th>{{ "Note" | t }}</th>
                        <th class="text-right">{{ "Work orders" | t }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(version, index) in recipe.versions"
                        :key="'version_' + version.id"
                        :class="{ 'current-version': isCurrent(version) }"
                    >
                        <td>{{ version.version_number }}</td>
                        <td>{{ formatDate(version.valid_from) }}</td>
                        <td>{{ version.valid_to ? formatDate(version.valid_to) : t("Valid now") }}</td>
                        <td
                            v-for="column in ingredientColumns(recipe)"
                            :key="version.id + '_' + column.product_code"
                            class="text-right"
                        >
                            {{ quantityOf(version, column.product_code) }}
                        </td>
                        <td>{{ version.note || "-" }}</td>
                        <td class="text-right">{{ version.work_order_count }}</td>
                        <td class="text-right">
                            <!-- Only the latest version that no work order uses can be deleted (a typo fix). -->
                            <v-btn
                                v-if="
                                    index === 0 &&
                                    version.work_order_count === 0 &&
                                    hasPermissionForAction(PermissionsConstants.RECIPE, PermissionsConstants.DELETE)
                                "
                                icon
                                small
                                :title="t('Delete')"
                                @click="confirmDelete(version)"
                            >
                                <v-icon small>delete</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card>

        <p v-if="!loadingRecipes && recipes.length === 0">{{ "No recipes" | t }}</p>

        <recipe-version-modal
            v-if="modalRecipe"
            :recipe="modalRecipe"
            @close="modalRecipe = null"
            @saved="modalRecipe = null"
        />

        <v-dialog :value="!!versionToDelete" max-width="420" persistent>
            <v-card v-if="versionToDelete">
                <v-card-title class="headline">{{ "Delete recipe version?" | t }}</v-card-title>
                <v-card-text>
                    {{ "The previous version becomes valid again with no end date." | t }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="versionToDelete = null">{{ "Cancel" | t }}</v-btn>
                    <v-btn dark color="bal-red" @click="deleteConfirmed">{{ "Delete" | t }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// Mix recipe list (BALTT-529): every recipe with its version history and per-mix ingredient quantities.
// Lets users with the "recipe" permission add a new version or delete the latest unused one.
// No props, no events.
import { mapActions, mapGetters, mapState } from "vuex";
import { PermissionsConstants } from "@/permissions";
import RecipeVersionModal from "./recipe-version-modal.vue";

export default {
    name: "recipe-list",
    components: { RecipeVersionModal },
    data() {
        return {
            modalRecipe: null,
            versionToDelete: null,
        };
    },
    computed: {
        ...mapState("RecipeStore", ["recipes", "loadingRecipes"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
    },
    async created() {
        await this.getRecipes();
    },
    methods: {
        ...mapActions("RecipeStore", ["getRecipes", "deleteVersion"]),
        // Union of the ingredients of all versions, in the order they first appear, so a column per ingredient
        // stays stable even if a later version adds or drops one.
        ingredientColumns(recipe) {
            const columns = [];
            [...recipe.versions].reverse().forEach((version) => {
                version.lines.forEach((line) => {
                    if (!columns.some((column) => column.product_code === line.product_code)) {
                        columns.push({ product_code: line.product_code, product_name: line.product_name });
                    }
                });
            });
            return columns;
        },
        quantityOf(version, productCode) {
            const line = version.lines.find((item) => item.product_code === productCode);
            return line ? line.quantity_per_mix.toLocaleString("et-EE", { maximumFractionDigits: 3 }) : "-";
        },
        isCurrent(version) {
            return !version.valid_to;
        },
        formatDate(isoDate) {
            if (!isoDate) return "-";
            const [year, month, day] = isoDate.split("-");
            return `${day}.${month}.${year}`;
        },
        openVersionModal(recipe) {
            this.modalRecipe = recipe;
        },
        confirmDelete(version) {
            this.versionToDelete = version;
        },
        async deleteConfirmed() {
            const versionId = this.versionToDelete.id;
            this.versionToDelete = null;
            await this.deleteVersion(versionId);
        },
    },
};
</script>

<style scoped>
.recipe-hint {
    color: #888;
}

.recipe-name {
    color: #888;
    font-weight: normal;
}

.current-version td {
    font-weight: bold;
}
</style>
